import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import './Scrapbook.css';

// Import audio
import audioFile from '../image/Alex Warren - Ordinary (Official Video).mp3';

function Scrapbook() {
  const navigate = useNavigate();
  const bookRef = useRef(null);
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [snowflakes, setSnowflakes] = useState([]);
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  // Import all photos using require.context
  const importAll = (r) => r.keys().map(r);
  const photos = importAll(require.context('../image/scrapbook', false, /\.jpg$/));

  const welcomeMessage = "Hey Rushi, this is your digital scrapbook. All the pictures I took of you through the years – the good, the bad, and the not so good. This one has unlimited uses, I hope you enjoy. Queue the music!";

  useEffect(() => {
    // Typewriter effect
    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      if (currentIndex <= welcomeMessage.length) {
        setDisplayedText(welcomeMessage.slice(0, currentIndex));
        currentIndex++;

        // Trigger music when "Queue the music!" is fully typed
        if (currentIndex === welcomeMessage.length && audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => {
            console.log('Audio autoplay prevented:', err);
          });
        }
      } else {
        setShowCursor(false);
        clearInterval(typeInterval);
      }
    }, 75);

    return () => clearInterval(typeInterval);
  }, [welcomeMessage]);

  useEffect(() => {
    // Create snowflakes
    const createSnowflakes = () => {
      const flakes = [];
      for (let i = 0; i < 50; i++) {
        flakes.push({
          id: i,
          left: Math.random() * 100,
          animationDuration: Math.random() * 3 + 5,
          animationDelay: Math.random() * 8,
          opacity: Math.random() * 0.5 + 0.3,
          fontSize: Math.random() * 10 + 10,
        });
      }
      setSnowflakes(flakes);
    };
    createSnowflakes();
  }, []);


  useEffect(() => {
    // Start automatic page flipping when music starts
    const songDuration = 187; // seconds
    const timePerFlip = 3.2; // seconds per flip
    const totalPages = photos.length + 2;
    const maxFlips = totalPages / 2 - 1;

    let currentFlip = 0;
    let flipInterval;
    let songEndTimeout;

    // Wait for the typewriter to finish and music to start
    const startFlippingDelay = welcomeMessage.length * 75; // typewriter duration

    const startFlippingTimeout = setTimeout(() => {
      flipInterval = setInterval(() => {
        if (bookRef.current && currentFlip < maxFlips) {
          currentFlip++;
          bookRef.current.pageFlip().flipNext();
        } else {
          clearInterval(flipInterval);

          // Calculate how much time has elapsed from flipping
          const elapsedTime = currentFlip * timePerFlip;

          // Navigate to tree 2 seconds before song finishes
          const timeUntilRedirect = Math.max(0, (songDuration - 2 - elapsedTime) * 1000);
          songEndTimeout = setTimeout(() => {
            localStorage.setItem('authenticated', 'true');
            navigate('/', { state: { showTree: true, fromScrapbook: true } });
          }, timeUntilRedirect);
        }
      }, timePerFlip * 1000);
    }, startFlippingDelay);

    return () => {
      clearTimeout(startFlippingTimeout);
      if (flipInterval) clearInterval(flipInterval);
      if (songEndTimeout) clearTimeout(songEndTimeout);
    };
  }, [photos.length, welcomeMessage.length, navigate]);

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="scrapbook">
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className="snowflake"
          style={{
            left: `${flake.left}%`,
            animationDuration: `${flake.animationDuration}s`,
            animationDelay: `${flake.animationDelay}s`,
            opacity: flake.opacity,
            fontSize: `${flake.fontSize}px`,
          }}
        >
          ❄
        </div>
      ))}

      <audio ref={audioRef} src={audioFile} />

      <button className="mute-button" onClick={toggleMute}>
        {isMuted ? '🔇' : '🔊'}
      </button>

      <div className="book-container">
        <HTMLFlipBook
          ref={bookRef}
          width={800}
          height={1000}
          size="stretch"
          minWidth={315}
          maxWidth={2000}
          minHeight={400}
          maxHeight={2500}
          maxShadowOpacity={0.2}
          showCover={false}
          mobileScrollSupport={false}
          flippingTime={800}
          drawShadow={false}
          useMouseEvents={false}
          className="flipbook"
          startPage={0}
        >
          {/* First page with typewriter message */}
          <div className="page">
            <div className="page-content">
              <p className="welcome-text">
                {displayedText}
                {showCursor && <span className="cursor">|</span>}
              </p>
            </div>
          </div>
          {/* Second empty page */}
          <div className="page">
            <div className="page-content"></div>
          </div>

          {/* Photo pages */}
          {photos.map((photo, index) => {
            // Extract date/time from filename
            // Format: 2024-12-01_00-21-20_1.jpg -> December 1, 2024 at 12:21 AM
            const filename = photo.split('/').pop().split('.')[0];
            const [datePart, timePart] = filename.split('_');
            const [year, month, day] = datePart.split('-');
            const [hour, minute] = timePart.split('-');

            const date = new Date(year, month - 1, day, hour, minute);
            const formattedDate = date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            const formattedTime = date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });

            return (
              <div key={index} className="page">
                <div className="page-content">
                  <img
                    src={photo}
                    alt={`Memory ${index + 1}`}
                    className="photo"
                  />
                  <div className="page-number">{formattedDate} at {formattedTime}</div>
                </div>
              </div>
            );
          })}
        </HTMLFlipBook>
      </div>
    </div>
  );
}

export default Scrapbook;
